"use client";

import React from "react";
import Image from "next/image";

interface StaticIconProps {
  /** Path relative to /public, e.g. "/Static Icons/icons8-star-ios-17-glyph/icons8-star-30.svg" */
  src: string;
  alt?: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Color mode for the icon. The SVGs ship as solid black glyphs.
   * "adaptive" → inverts to white in dark mode automatically via CSS filter.
   * "color"    → no filter applied (use for colored/multi-tone icons).
   * "muted"    → rendered at reduced opacity for secondary/muted contexts.
   */
  variant?: "adaptive" | "color" | "muted";
}

/**
 * StaticIcon — renders an icons8 glyph SVG with proper dark-mode support.
 *
 * The icons8 iOS-17 glyph SVGs are solid black shapes. In light mode they
 * render as-is. In dark mode we apply `invert` so they become white, matching
 * the text/icon color convention of the design system.
 */
const StaticIcon = React.memo(function StaticIcon({
  src,
  alt = "",
  size = 18,
  className = "",
  style,
  variant = "adaptive",
}: StaticIconProps) {
  const filterClass =
    variant === "adaptive"
      ? "dark:invert opacity-75"
      : variant === "muted"
        ? "opacity-40 dark:invert"
        : "";

  return (
    <Image
      src={src}
      alt={alt}
      width={size}
      height={size}
      className={`shrink-0 select-none ${filterClass} ${className}`}
      style={style}
      draggable={false}
      unoptimized
    />
  );
});

export default StaticIcon;

// ─── Canonical icon path helpers ───────────────────────────────────────────
// Pre-resolved paths for every available static icon, sized for the most
// common UI usage (30px = compact / 120px = large display).

export const STATIC_ICONS = {
  account:          "/Static Icons/icons8-account-ios-17-glyph/icons8-account-30.svg",
  addToCollection:  "/Static Icons/icons8-add-to-collection-ios-17-glyph/icons8-add-to-collection-30.svg",
  circle:           "/Static Icons/icons8-circle-ios-17-filled/icons8-circle-30.svg",
  edit:             "/Static Icons/icons8-edit-ios-17-glyph/icons8-edit-30.svg",
  ereader:          "/Static Icons/icons8-ereader-ios-17-glyph/icons8-ereader-30.svg",
  gallery:          "/Static Icons/icons8-gallery-ios-17-glyph/icons8-gallery-30.svg",
  verifiedBadge:    "/Static Icons/icons8-instagram-verification-badge-ios-17-glyph/icons8-instagram-verification-badge-30.svg",
  locked:           "/Static Icons/icons8-locked-ios-17-glyph/icons8-locked-30.svg",
  mail:             "/Static Icons/icons8-mail-ios-17-glyph/icons8-mail-30.svg",
  menu:             "/Static Icons/icons8-menu-ios-17-glyph/icons8-menu-30.svg",
  pin:              "/Static Icons/icons8-pin-ios-17-glyph/icons8-pin-30.svg",
  reading:          "/Static Icons/icons8-reading-ios-17-glyph/icons8-reading-30.svg",
  searchFolder:     "/Static Icons/icons8-search-folder-ios-17-glyph/icons8-search-folder-30.svg",
  settings:         "/Static Icons/icons8-setting-ios-17-glyph/icons8-setting-30.svg",
  signOut:          "/Static Icons/icons8-sign-out-ios-17-glyph/icons8-sign-out-30.svg",
  star:             "/Static Icons/icons8-star-ios-17-glyph/icons8-star-30.svg",
  trash:            "/Static Icons/icons8-trash-ios-17-glyph/icons8-trash-30.svg",
  /** Liquid-glass “1” — workspace switcher / primary workspace mark */
  workspaceLiquid:
    "/Static Icons/icons8-number-1-liquid-glass/icons8-number-1-48.svg",
} as const;

// Large display variants (120px artboard, same visual path)
export const STATIC_ICONS_LG = {
  account:          "/Static Icons/icons8-account-ios-17-glyph/icons8-account-120.svg",
  addToCollection:  "/Static Icons/icons8-add-to-collection-ios-17-glyph/icons8-add-to-collection-120.svg",
  edit:             "/Static Icons/icons8-edit-ios-17-glyph/icons8-edit-120.svg",
  ereader:          "/Static Icons/icons8-ereader-ios-17-glyph/icons8-ereader-120.svg",
  gallery:          "/Static Icons/icons8-gallery-ios-17-glyph/icons8-gallery-120.svg",
  locked:           "/Static Icons/icons8-locked-ios-17-glyph/icons8-locked-120.svg",
  mail:             "/Static Icons/icons8-mail-ios-17-glyph/icons8-mail-120.svg",
  menu:             "/Static Icons/icons8-menu-ios-17-glyph/icons8-menu-120.svg",
  pin:              "/Static Icons/icons8-pin-ios-17-glyph/icons8-pin-120.svg",
  reading:          "/Static Icons/icons8-reading-ios-17-glyph/icons8-reading-120.svg",
  searchFolder:     "/Static Icons/icons8-search-folder-ios-17-glyph/icons8-search-folder-120.svg",
  settings:         "/Static Icons/icons8-setting-ios-17-glyph/icons8-setting-120.svg",
  signOut:          "/Static Icons/icons8-sign-out-ios-17-glyph/icons8-sign-out-120.svg",
  star:             "/Static Icons/icons8-star-ios-17-glyph/icons8-star-120.svg",
  trash:            "/Static Icons/icons8-trash-ios-17-glyph/icons8-trash-120.svg",
} as const;
